import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { FiltersHttpService } from '../../services/filters-http.service';
import { FiltersActionTypes, LoadFilters, LoadFiltersFailure, LoadFiltersSuccess } from './filters.actions';
import { State } from '../state';
import { getFilterParams } from '../selectors';
import { PriceTypes, PriceTypeService } from '~shared/modules/price-type-toggler/price-type-toggler.module';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { getCategories } from '~app/modules/generic/store';
import { Category } from '~app/modules/generic/models';
import { isPlatformBrowser } from '@angular/common';
import { RegionSelectorService } from '~shared/modules/region-selector/services';

@Injectable()
export class FiltersEffects {
  validUserRegion = true;
  selectedRegion: string;
  @Effect()
  loadFilters$ = this.actions$.pipe(
    ofType(FiltersActionTypes.LOAD_FILTERS),
    map((action: LoadFilters) => action.payload),
    switchMap(({ filters, queryParams }) => {
      const isCategoryPage = this.productsListUrlService.isCategoryPage();
      return this.filtersHttpService
        .getFilters(filters, queryParams, isCategoryPage, this.selectedRegion ? this.selectedRegion : null)
        .pipe(
          map((response) => {
            return new LoadFiltersSuccess({
              filters: {
                ...response,
              },
            });
          }),
          catchError((error) => of(new LoadFiltersFailure({ error })))
        );
    })
  );
  private filterParams$ = this.store.pipe(select(getFilterParams));
  private priceType$ = this.priceTypeSvc.priceType$;
  private userRegion$ = this.regionSelectorService.userRegion$.pipe(
    tap((userRegion: string) => {
      if (!userRegion || userRegion === '') {
        this.validUserRegion = false;
      } else {
        this.selectedRegion = userRegion;
      }
    })
  );

  private sources: Observable<any>[] = [
    this.actions$.pipe(ofType(ROUTER_NAVIGATED)),
    this.priceType$,
    this.store.pipe(select(getCategories)),
    ...(this.validUserRegion ? [this.userRegion$] : []),
  ];
  private navigatedToCatalog = combineLatest(this.sources).pipe(
    map(([action, priceType, categories]: any[]) => {
      return {
        url: action.payload.routerState.url,
        queryParams: action.payload.routerState.queryParams,
        priceType,
        categories,
      };
    }),
    withLatestFrom(this.filterParams$, ({ url, queryParams, priceType, categories }, filters) => {
      return { url, queryParams, priceType, categories, filters };
    }),
    filter(({ url, queryParams, priceType, categories, filters }) => {
      if (!this.isValidParameters(priceType, categories)) {
        return false;
      }

      if (this.productsListUrlService.isCustomPLPPage() && Object.keys(queryParams).length === 0) {
        return false;
      }

      return true;
    }),
    map(({ filters, priceType, categories, queryParams }) => {
      if (this.productsListUrlService.isCategoryPage()) {
        const categorySlug = this.productsListUrlService.getCategory();
        filters = {
          ...filters,
          categories: [categorySlug]
        };
      } else if (this.productsListUrlService.isSearchPage()) {
        filters = {
          ...filters,
          top: { phrase: this.productsListUrlService.getSearchTerm() },
        };
      } else if (this.productsListUrlService.isBrandPLPPage()) {
        filters = {
          ...filters,
          options: {
            ...filters?.options,
            brand: [this.productsListUrlService.getBrandSlug()],
          },
        };
      }

      return { filters: { ...filters, priceType }, queryParams };
    })
  );

  @Effect({ dispatch: false })
  loadFiltersByRoute$ = this.navigatedToCatalog.pipe(
    tap(({ filters, queryParams }) => {
      if (isPlatformBrowser(this.platformId)) {
        this.store.dispatch(
          new LoadFilters({
            filters,
            queryParams,
          })
        );
      }
    })
  );

  constructor(
    private actions$: Actions,
    private priceTypeSvc: PriceTypeService,
    private filtersHttpService: FiltersHttpService,
    private store: Store<State>,
    private productsListUrlService: ProductsListUrlService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private regionSelectorService: RegionSelectorService
  ) {}

  private isValidParameters(priceType: PriceTypes, categories: Category[]): boolean {
    if (!this.productsListUrlService.isProductListPage()) {
      return false;
    }

    if (priceType == null) {
      return false;
    }

    return categories?.length !== 0;
  }
}
